import React, { PureComponent } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import LessonChildWorkspace from '~/components/courses/lesson-child-workspace';
import NodeHeader from '~/components/common/node-header';
import NewEntity from '~/components/common/new-entity';
import {
	setActiveLesson,
	toggleExpandEntity,
} from '~/redux/actions/workspaceActions';

/**
 * Lesson component for the workspace. The component acts as a droppable for Topics and Quizzes
 */
class LessonWorkspace extends PureComponent {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		/**
		 * Our current state which contains the following
		 * - ID: lesson ID
		 * - expanded: whether that node is expanded
		 * - isDropDisabled: whether the Droppable is enabled or not
		 * - workspace: current workspace
		 */
		this.state = {
			ID: this.props.lesson.ID,
			expanded:
				-1 !==
				this.props.workspace.present.expandedItems.indexOf(
					this.props.lesson.ID
				),
			isDropDisabled: this.props.lesson.isLessonDropDisabled,
			workspace: this.props.workspace.present,
		};
	}

	/**
	 * Check when we need to update the workspace to allow droppables.
	 *
	 * @param {Object} props
	 * @param {Object} state
	 */
	static getDerivedStateFromProps( props, state ) {
		const workspace = props.workspace.present;
		const prevWorkspace = state.workspace;
		const stateObject = {
			workspace,
		};

		// Listen to changes to the expanded state
		if ( workspace.expandedItems !== prevWorkspace.expandedItems ) {
			stateObject.expanded =
				-1 !== workspace.expandedItems.indexOf( state.ID );
		}

		if (
			workspace.currentDraggableEntity !== state.currentDraggableEntity &&
			workspace.currentDraggableEntity
		) {
			let isDropDisabled = true;

			// Only Draggable topics and quizzes can be dropped on that Droppable.
			if (
				'topic' === workspace.currentDraggableEntity.type ||
				'quiz' === workspace.currentDraggableEntity.type ||
				'sfwd-topic' === workspace.currentDraggableEntity.type ||
				'sfwd-quiz' === workspace.currentDraggableEntity.type
			) {
				isDropDisabled = false;
			}

			stateObject.isDropDisabled = isDropDisabled;
		}

		return stateObject;
	}

	/**
	 * Render function
	 */
	render() {
		const { expanded } = this.state;
		const {
			lesson,
			index,
			workspace,
			setActiveLesson,
			toggleExpandEntity,
		} = this.props;

		return (
			// A lesson must be draggable for us to sort them
			<Draggable
				draggableId={ JSON.stringify( { ...lesson } ) }
				index={ index }
				key={ lesson.ID }
			>
				{ ( provided ) => (
					<div
						key={ lesson.ID }
						ref={ provided.innerRef }
						{ ...provided.draggableProps }
						className={ classNames( {
							'ld__builder--parent': true,
							'ld__builder--lesson': true,
							'ld__builder--section-heading':
								'section-heading' === lesson.type,
							'-active':
								workspace.present.activeLesson === lesson.ID,
							'-expanded': expanded,
						} ) }
						onClick={ () => {
							if ( 'section-heading' !== lesson.type ) {
								setActiveLesson( {
									activeLesson: lesson.ID,
								} );
							}
						} }
					>
						<NodeHeader
							node={ lesson }
							index={ index }
							provided={ provided }
							expanded={ expanded }
							className={ classNames( {
								'ld__builder-row-type': true,
								'-parent': true,
							} ) }
							toggleNode={ ( e ) => {
								e.preventDefault();
								toggleExpandEntity( index, lesson );
							} }
						/>

						{ 'section-heading' !== lesson.type && (
							<div
								className={ classNames( {
									'ld__builder--child': true,
									'-expanded': expanded,
								} ) }
								key={ lesson.ID }
							>
								{ /* Our tree containing topics and quizzes */ }
								<Droppable
									droppableId={ JSON.stringify( {
										ID: lesson.ID,
										type: 'lesson',
										dnd: 'droppable-topic',
									} ) }
									isDropDisabled={ this.state.isDropDisabled }
								>
									{ ( dropProvided, dropSnapshot ) => (
										<div
											ref={ dropProvided.innerRef }
											className={ classNames( {
												'-is-dragging-over':
													dropSnapshot.isDraggingOver,
											} ) }
										>
											{ lesson.tree.map(
												( node, indexLesson ) => (
													<LessonChildWorkspace
														key={ node.ID }
														node={ node }
														indexLesson={
															indexLesson
														}
														index={ index }
													/>
												)
											) }

											{ /* Placeholder for dropping new topics/quizzes */ }
											<div
												className="ld__builder--placeholder"
												data-label={ `${ LearnDashData.i18n.drop_quizzes_topics }` }
											>
												{ dropProvided.placeholder }
											</div>
										</div>
									) }
								</Droppable>

								<div className="ld__builder--new-entities">
									<NewEntity
										type="topic"
										parentLesson={ lesson.ID }
									/>
									<NewEntity
										type="quiz"
										parentLesson={ lesson.ID }
									/>
								</div>
							</div>
						) }
					</div>
				) }
			</Draggable>
		);
	}
}

/**
 * Valid props
 */
LessonWorkspace.propTypes = {
	lesson: PropTypes.object,
	index: PropTypes.number,
	setActiveLesson: PropTypes.func,
	toggleExpandEntity: PropTypes.func,
	workspace: PropTypes.shape( {
		present: PropTypes.object,
	} ),
	data: PropTypes.shape( {
		labels: PropTypes.object,
	} ),
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 * @param {*} dispatch
 */
const mapDispatchToProps = ( dispatch ) => ( {
	setActiveLesson: ( id ) => dispatch( setActiveLesson( id ) ),
	toggleExpandEntity: ( indexLesson, node ) =>
		dispatch( toggleExpandEntity( indexLesson, node ) ),
} );

export default connect(
	mapStateToProps,
	mapDispatchToProps
)( LessonWorkspace );
